import { useCallback, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import { useAppContext } from '../../../contexts/AppContext';
import {
  blockAdminOrganization,
  saveAdminOrganizationNote,
  unblockAdminOrganization,
} from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';
import { useAdminOrganizationDetail } from '../hooks/useAdminOrganizationDetail';
import { OrganizationStatusBadge } from './OrganizationStatusBadge';
import { OrganizationKpiRow } from './OrganizationKpiRow';
import { UsageSection } from './sections/UsageSection';
import { BillingSection } from './sections/BillingSection';
import { AccessControlSection } from './sections/AccessControlSection';
import { TeamSection } from './sections/TeamSection';
import { ChannelsSection } from './sections/ChannelsSection';
import { TicketsSection } from './sections/TicketsSection';
import { NotesSection } from './sections/NotesSection';
import { AuditSection } from './sections/AuditSection';
import { ImpersonationStartDialog } from './ImpersonationStartDialog';

export function AdminOrganizationDetailPage() {
  const { closeSuperadminOrg } = useAppContext();
  const { data, isLoading, error, refetch, orgId } = useAdminOrganizationDetail();
  const [isBlockSaving, setIsBlockSaving] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  const handleAccessAccount = useCallback(() => {
    setImpersonateOpen(true);
  }, []);

  const handleBlock = useCallback(
    async (reason: string) => {
      if (!orgId) return;
      setIsBlockSaving(true);
      try {
        await blockAdminOrganization(orgId, reason);
        notify.success('Empresa bloqueada.');
        await refetch();
      } catch (e) {
        notify.error(
          e instanceof SuperadminApiError ? e.message : 'Falha ao bloquear empresa'
        );
      } finally {
        setIsBlockSaving(false);
      }
    },
    [orgId, refetch]
  );

  const handleUnblock = useCallback(
    async (reason: string) => {
      if (!orgId) return;
      setIsBlockSaving(true);
      try {
        await unblockAdminOrganization(orgId, reason);
        notify.success('Empresa desbloqueada.');
        await refetch();
      } catch (e) {
        notify.error(
          e instanceof SuperadminApiError ? e.message : 'Falha ao desbloquear empresa'
        );
      } finally {
        setIsBlockSaving(false);
      }
    },
    [orgId, refetch]
  );

  const handleSaveNote = useCallback(
    async (body: string) => {
      if (!orgId) return;
      setIsNoteSaving(true);
      try {
        await saveAdminOrganizationNote(orgId, body);
        notify.success('Nota salva.');
        await refetch();
      } catch (e) {
        notify.error(
          e instanceof SuperadminApiError ? e.message : 'Falha ao salvar nota'
        );
      } finally {
        setIsNoteSaving(false);
      }
    },
    [orgId, refetch]
  );

  const displayEmail =
    data?.org.ownerEmail ?? data?.org.businessEmail ?? undefined;

  return (
    <div className="flex flex-col bg-background">
      <PageHeader
        title={
          data ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>{data.org.name}</span>
              <OrganizationStatusBadge row={data.org} />
            </span>
          ) : (
            'Empresa'
          )
        }
        subtitle={displayEmail}
        titleClassName="text-xl font-semibold tracking-tight text-default"
        subtitleClassName="text-[13px] text-muted"
        className="mb-[18px]"
        action={
          data ? (
            <Button type="button" size="sm" onClick={handleAccessAccount}>
              Acessar conta
            </Button>
          ) : undefined
        }
      />

      {!orgId && (
        <Alert>
          <AlertTitle>Empresa não selecionada</AlertTitle>
          <AlertDescription className="flex flex-col gap-s3">
            <p>Selecione uma empresa na lista para ver os detalhes.</p>
            <Button variant="outline" size="sm" onClick={closeSuperadminOrg}>
              Voltar às empresas
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {orgId && isLoading && (
        <div className="flex items-center justify-center py-s16" role="status" aria-live="polite">
          <Spinner color="primary" size="lg" />
        </div>
      )}

      {orgId && !isLoading && error && (
        <Alert variant="destructive">
          <AlertTitle>Erro ao carregar</AlertTitle>
          <AlertDescription className="flex flex-col gap-s3">
            <p>{error}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {orgId && !isLoading && !error && data && (
        <>
          <OrganizationKpiRow org={data.org} usage={data.usage} />
          <UsageSection usage={data.usage} />
          <BillingSection products={data.products} invoices={data.invoices} />
          <AccessControlSection
            subscriptionStatus={data.org.subscriptionStatus}
            blockedAt={data.org.blockedAt}
            trialEndsAt={data.org.trialEndsAt}
            onBlock={handleBlock}
            onUnblock={handleUnblock}
            isSaving={isBlockSaving}
          />
          <TeamSection team={data.team} organizationId={orgId} onRefetch={refetch} />
          <ChannelsSection
            channels={data.channels}
            whatsappInstances={data.whatsappInstances}
          />
          <TicketsSection tickets={data.tickets} />
          <NotesSection
            note={data.note}
            onSave={handleSaveNote}
            isSaving={isNoteSaving}
          />
          <AuditSection audit={data.audit} />
        </>
      )}
      <ImpersonationStartDialog
        open={impersonateOpen}
        org={data ? { id: data.org.id, name: data.org.name } : null}
        onClose={() => setImpersonateOpen(false)}
      />
    </div>
  );
}
